import React, { useState, useRef, useEffect } from "react";
import styles from "../../FilePreviewModal.module.scss";
import { EThemeType } from "redux/reducers";
import classNames from "classnames";

interface CodePreviewProps {
  content: string;
  language: string;
  theme?: EThemeType;
  fileName?: string;
}

// Utility functions
const highlightCode = (content: string, language: string): string => {
  // For production use, consider a library like highlight.js or prism.js
    if (language === "eml" || language === "msg") {
        const keyClass = `${language}-key`;
        const stringClass = `${language}-string`;
    
        return content
          .replace(/"([^"]+)":/g, `<span class="${keyClass}">"$1":</span>`)
          .replace(/: "([^"]+)"/g, `: <span class="${stringClass}">"$1"</span>`);
      }
  return content;
};

const TxtPreview: React.FC<CodePreviewProps> = ({ content, language, theme, fileName }) => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [displayedContent, setDisplayedContent] = useState<string>(content);
  const codeRef = useRef<HTMLElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    setDisplayedContent(content);
  }, [content]);

  // Handle search with special handling for JSON
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    
    if (!codeRef.current) return;
    
    try {
      const term = e.target.value.trim();
      
      if (term === "") {
        if (language === "eml" || language === "msg") {
          codeRef.current.innerHTML = highlightCode(displayedContent, language);
        } else {
          codeRef.current.textContent = displayedContent;
        }
        return;
      }

      if (language === "eml" || language === "msg") {
        codeRef.current.innerHTML = highlightCode(displayedContent, language);
        
        // Then find and highlight text nodes only
        const walker = document.createTreeWalker(
          codeRef.current,
          NodeFilter.SHOW_TEXT,
          null
        );
        
        const nodesToReplace = [];
        let currentNode;
        const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(escapedTerm, 'gi');
        
        // First collect all nodes that need replacement
        while (currentNode = walker.nextNode()) {
          if (regex.test(currentNode.nodeValue || '')) {
            nodesToReplace.push(currentNode);
          }
          // Reset regex for next test
          regex.lastIndex = 0;
        }
        
        // Then replace them
        for (const node of nodesToReplace) {
          const parent = node.parentNode;
          if (!parent) continue;
          
          const text = node.nodeValue || '';
          const fragment = document.createDocumentFragment();
          
          let lastIndex = 0;
          let match;
          regex.lastIndex = 0;
          
          while ((match = regex.exec(text)) !== null) {
            // Add text before match
            if (match.index > lastIndex) {
              fragment.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
            }
            
            // Add highlighted match
            const mark = document.createElement('mark');
            mark.className = styles.searchHighlight;
            mark.textContent = match[0];
            fragment.appendChild(mark);
            
            lastIndex = regex.lastIndex;
          }
          
          // Add remaining text
          if (lastIndex < text.length) {
            fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
          }
          
          parent.replaceChild(fragment, node);
        }
      } else {
        // For non-JSON files, we can use a simpler approach
        const tempDiv = document.createElement('div');
        tempDiv.textContent = displayedContent;
        const safeContent = tempDiv.textContent || '';
        
        // Escape regex special characters
        const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(escapedTerm, 'gi');
        
        // Simple replacement with highlighting
        const highlightedContent = safeContent.replace(
          regex, 
          match => `<mark class="${styles.searchHighlight}">${match}</mark>`
        );
        
        codeRef.current.innerHTML = highlightedContent;
      }
    } catch (error) {
      console.error("Search error:", error);
      // Fallback to displaying the content without highlighting
      if (language === "eml" || language === "msg") {
        codeRef.current.innerHTML = highlightCode(displayedContent, language);
      } else {
        codeRef.current.textContent = displayedContent;
      }
    }
  };

  // Download file
  const handleDownload = () => {
    const blob = new Blob([displayedContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    let downloadName = fileName ? String(fileName) : `file.${language === 'eml' ? 'eml' : language === 'msg' ? 'msg' : 'txt'}`;
  
  // If fileName doesn't have an extension, add one based on the language
  if (fileName && !String(fileName).includes('.')) {
    downloadName += `.${language === 'eml' ? 'eml' : language === 'msg' ? 'msg' : 'txt'}`;
  }
  
  a.download = downloadName; // This should now be a string primitive
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Open in browser
  const handleOpenInBrowser = () => {
    const blob = new Blob([displayedContent], { type: language === 'eml' ? 'application/eml' : language === 'msg' ? 'application/msg' : 'text/plain' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    // Don't revoke immediately to allow browser to load it
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  // Ensure scrolling works
  useEffect(() => {
    if (containerRef.current) {
      // Make sure container has proper overflow
      containerRef.current.style.overflow = 'auto';
      
      // Ensure the code doesn't wrap and scrollbars appear when needed
      if (codeRef.current && codeRef.current.parentElement) {
        codeRef.current.parentElement.style.whiteSpace = 'pre';
        codeRef.current.parentElement.style.overflow = 'visible';
        codeRef.current.style.display = 'inline-block';
        codeRef.current.style.minWidth = 'fit-content';
      }
    }
  }, []);

  // Render initial content and whenever content or language changes
  useEffect(() => {
    if (codeRef.current) {
      if (language === "eml" || language === "msg" ) {
        codeRef.current.innerHTML = highlightCode(displayedContent, language);
      } else {
        codeRef.current.textContent = displayedContent;
      }
    }
  }, [displayedContent, language]);

  return (
    <div className={styles.codePreviewWrapper}>
      <div className={styles.codePreviewContainer}>
        <div className={styles.codeToolbar}>
          <div className={styles.toolbarRight}>
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={handleSearch}
              className={styles.searchInput}
            />
            
            <button 
              className={styles.toolbarButton}
              onClick={handleDownload}
              title="Download file"
            >
              Download
            </button>
            
            {language !== "eml" && language !== "msg" && (
            <button
              className={styles.toolbarButton}
              onClick={handleOpenInBrowser}
              title="Open in browser"
            >
              Open
            </button>
          )}
          </div>
        </div>
        <div 
          ref={containerRef}
          className={classNames(styles.codeContainer, {
            [styles.light]: theme === "light",
            [styles.dark]: theme === "dark"
          })}
          style={{ 
            overflow: 'auto',
            height: '445px',
            maxHeight: '445px' 
          }}
        >
          <pre className={styles.codeContent} style={{ overflow: 'visible', whiteSpace: 'pre' }}>
            <code ref={codeRef} style={{ display: 'inline-block', minWidth: 'fit-content' }}></code>
          </pre>
        </div>
      </div>
    </div>
  );
};

export default TxtPreview;